import React, { useEffect, useRef, useState } from "react";
import "./ExamCategorySlider.css";
import { useFavoriteCategories } from "../hooks/useFavoriteCategories";
import { useIsExamSelectionPending, useSetUserExamCategory } from "../hooks/useSetUserExamCategory";
import { useNavigate } from "react-router-dom";
import { DASHBOARD_ROUTES } from "./dashboardRoutes";

const ExamCategorySlider = ({ onActiveCategoryChange }) => {
  const navigate = useNavigate();
  const [activeCategory, setActiveCategory] = useState(null);
  const [hasReachedEnd, setHasReachedEnd] = useState(false);
  const [loadError, setLoadError] = useState("");
  const trackRef = useRef(null);
  const itemRefs = useRef({});
  const {
    data: categories = [],
    isLoading,
    isError,
    error,
  } = useFavoriteCategories();
  const setUserExamCategoryMutation = useSetUserExamCategory();
  const isSaving = useIsExamSelectionPending();
  const addCategoryButton = (
    <button type="button" className="exam-category-slider__nav addCategoryBTN"
      aria-label="Add exam category" disabled={isSaving}
      onClick={() => navigate(DASHBOARD_ROUTES.examCategories)}>
      <i className="bi bi-plus" aria-hidden="true" /> Add Category
    </button>
  );


  const syncNavState = () => {
    const track = trackRef.current;
    if (!track) return;

    const { scrollLeft, scrollWidth, clientWidth } = track;
    const isAtStart = scrollLeft <= 1;
    const isAtEnd = scrollLeft + clientWidth >= scrollWidth - 1;
    const hasScrollableOverflow = scrollWidth - clientWidth > 1;

    setHasReachedEnd((prev) => {
      if (!hasScrollableOverflow) {
        return false;
      }

      if (isAtEnd) {
        return true;
      }

      if (isAtStart) {
        return false;
      }

      return prev;
    });
  };

  useEffect(() => {
    const defaultCategory =
      categories.find((category) => Number(category.default_category) === 1) ||
      categories[0] ||
      null;

    setActiveCategory((previousActiveCategory) => {
      if (
        previousActiveCategory !== null &&
        categories.some(
          (category) => Number(category.category_id) === Number(previousActiveCategory)
        )
      ) {
        return previousActiveCategory;
      }

      return defaultCategory?.category_id ?? null;
    });
  }, [categories]);

  useEffect(() => {
    if (!categories.length || activeCategory === null) {
      return;
    }

    const selectedCategory = categories.find(
      (category) => Number(category.category_id) === Number(activeCategory)
    );

    if (selectedCategory && typeof onActiveCategoryChange === "function") {
      onActiveCategoryChange(selectedCategory);
    }
  }, [activeCategory, categories, onActiveCategoryChange]);

  useEffect(() => {
    if (!categories.length || activeCategory === null) {
      return;
    }

    const activeNode = itemRefs.current[activeCategory];
    if (activeNode && typeof activeNode.scrollIntoView === "function") {
      const isLastCategory =
        Number(activeCategory) === Number(categories[categories.length - 1]?.category_id);

      activeNode.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: isLastCategory ? "end" : "center",
      });
    }
    requestAnimationFrame(syncNavState);
  }, [activeCategory, categories]);

  useEffect(() => {
    syncNavState();

    const track = trackRef.current;
    if (!track) return undefined;

    const handleScroll = () => syncNavState();
    track.addEventListener("scroll", handleScroll, { passive: true });
    window.addEventListener("resize", syncNavState);

    return () => {
      track.removeEventListener("scroll", handleScroll);
      window.removeEventListener("resize", syncNavState);
    };
  }, []);

  const handleScroll = (direction) => {
    if (!trackRef.current) return;

    const scrollAmount = 240;
    trackRef.current.scrollBy({
      left: direction * scrollAmount,
      behavior: "smooth",
    });
  };

  const handleCategorySelect = async (category) => {
    const categoryId = Number(category.category_id);

    if (!categoryId || isSaving || Number(activeCategory) === categoryId) {
      return;
    }

    const previousActiveCategory = activeCategory;
    setActiveCategory(categoryId);
    setLoadError("");

    try {
      const { meta } = await setUserExamCategoryMutation.mutateAsync({
        exam_category_id: categoryId,
      });

      if (!categories.length && meta?.message) {
        setLoadError(meta.message);
      }
    } catch (error) {
      setActiveCategory(previousActiveCategory);
      setLoadError(error.message || "Unable to update the selected category.");
    }
  };

  const resolvedLoadError = loadError || (isError
    ? error?.response?.data?.message || error?.message || "Unable to load categories."
    : "");

  if (isLoading && !categories.length) {
    return (
      <section className="exam-category-slider" aria-label="Exam categories">
        <div className="exam-category-slider__trackWrap">
          <div className="exam-category-slider__status">Loading categories...</div>
        </div>
      </section>
    );
  }

  if (resolvedLoadError && !categories.length) {
    return (
      <section className="exam-category-slider" aria-label="Exam categories">
        <div className="exam-category-slider__trackWrap">
          <div role="alert" className="exam-category-slider__status">{resolvedLoadError}</div>
          {addCategoryButton}
        </div>
      </section>
    );
  }

  if (!categories.length) {
    return (
      <section className="exam-category-slider" aria-label="Exam categories">
        <div className="exam-category-slider__trackWrap">
          <div className="exam-category-slider__status">No categories available.</div>
          {addCategoryButton}
        </div>
      </section>
    );
  }

  return (
    <section className="exam-category-slider" aria-label="Exam categories">
      <div className="exam-category-slider__background" aria-hidden="true" />

      <div className="exam-category-slider__trackWrap">
        {hasReachedEnd && (
          <button
            type="button"
            className="exam-category-slider__nav exam-category-slider__nav--prev"
            aria-label="Scroll categories left"
            onClick={() => handleScroll(-1)}
          >
            <i className="bi bi-chevron-left" aria-hidden="true" />
          </button>
        )}

        <div className="exam-category-slider__track" ref={trackRef}>
          {categories.map((category) => {
            const categoryId = Number(category.category_id);
            const isActive = Number(activeCategory) === categoryId;

            return (
              <button
                key={category.category_id}
                ref={(node) => {
                  if (node) {
                    itemRefs.current[categoryId] = node;
                  }
                }}
                type="button"
                className={`exam-category-slider__item ${
                  isActive ? "is-active" : ""
                }`}
                disabled={isSaving}
                onClick={() => handleCategorySelect(category)}
                aria-pressed={isActive}
              >
                <span className="exam-category-slider__icon" aria-hidden="true">
                  {category.icon_url ? (
                    <img src={category.icon_url} alt="" className="exam-category-slider__icon-image" />
                  ) : (
                    <i className="bi bi-grid-fill" />
                  )}
                </span>
                <span className="exam-category-slider__label">
                  {category.name}
                </span>
              </button>
            );
          })}
        </div>

        {!hasReachedEnd && (
          <button type="button" className="exam-category-slider__nav exam-category-slider__nav--next"
            aria-label="Scroll categories right" onClick={() => handleScroll(1)}>
            <i className="bi bi-chevron-right" aria-hidden="true" />
          </button>
        )}
        {addCategoryButton}
      </div>
      {isSaving ? (
        <div className="exam-category-slider__saveState">Updating selected category...</div>
      ) : null}
      {!isSaving && resolvedLoadError ? (
        <div className="exam-category-slider__saveState">{resolvedLoadError}</div>
      ) : null}
    </section>
  );
};

export default ExamCategorySlider;
